import React from 'react';
import { FileText, Globe, Layers, MessageSquare, ScrollText, Tags, Upload, type LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

export type AdminSectionId = 'staff' | 'upload' | 'documents' | 'tags' | 'queue' | 'seeds' | 'opinions';

interface AdminSection {
  id: AdminSectionId;
  label: string;
  description: string;
  icon: LucideIcon;
}

interface AdminNavGroup {
  label: string;
  items: AdminSection[];
}

export const ADMIN_NAV_GROUPS: AdminNavGroup[] = [
  {
    label: 'To address',
    items: [
      {
        id: 'staff',
        label: 'Staff questions',
        description: 'Open questions sent to the RCM team. An answer is published in the Library.',
        icon: MessageSquare,
      },
    ],
  },
  {
    label: 'Library',
    items: [
      {
        id: 'upload',
        label: 'Upload',
        description: 'Add a PDF to the shared knowledge base used in chat.',
        icon: Upload,
      },
      {
        id: 'documents',
        label: 'Documents',
        description: 'Search, edit, and remove documents already in the library.',
        icon: FileText,
      },
      {
        id: 'tags',
        label: 'Tags',
        description: 'Manage the tags used to classify library content.',
        icon: Tags,
      },
    ],
  },
  {
    label: 'Opinions',
    items: [
      {
        id: 'opinions',
        label: 'Opinion articles',
        description: 'Paste a source, track its export pull request, and sync published articles from the knowledge base.',
        icon: ScrollText,
      },
    ],
  },
  {
    label: 'Crawler',
    items: [
      {
        id: 'queue',
        label: 'Queue',
        description: 'How many crawl jobs are waiting or in flight.',
        icon: Layers,
      },
      {
        id: 'seeds',
        label: 'Seeds',
        description: 'Pages queued for a single-page crawl, including chat web-search citations.',
        icon: Globe,
      },
    ],
  },
];

const ADMIN_SECTIONS = ADMIN_NAV_GROUPS.flatMap((group) => group.items);

export function parseAdminSection(value: string | null): AdminSectionId {
  const match = ADMIN_SECTIONS.find((section) => section.id === value);
  return match?.id ?? 'staff';
}

export function adminSectionById(id: AdminSectionId): AdminSection {
  return ADMIN_SECTIONS.find((section) => section.id === id) ?? ADMIN_SECTIONS[0];
}

interface AdminSectionNavProps {
  active: AdminSectionId;
  onSelect: (id: AdminSectionId) => void;
}

export function AdminSectionNav({ active, onSelect }: AdminSectionNavProps) {
  return (
    <nav className="flex min-h-0 flex-1 flex-col overflow-y-auto px-2 pb-4" aria-label="Admin">
      {ADMIN_NAV_GROUPS.map((group) => (
        <div key={group.label} className="mb-4">
          <p className="px-2 py-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
            {group.label}
          </p>
          <div className="space-y-0.5">
            {group.items.map((item) => {
              const selected = item.id === active;
              const Icon = item.icon;
              return (
                <button
                  key={item.id}
                  type="button"
                  aria-current={selected ? 'page' : undefined}
                  onClick={() => onSelect(item.id)}
                  className={cn(
                    'flex w-full items-center gap-2.5 rounded-lg px-2 py-2 text-left transition-colors',
                    selected
                      ? 'bg-[hsl(var(--primary)/0.12)] text-primary'
                      : 'text-foreground/90 hover:bg-muted/60',
                  )}
                >
                  <span
                    className={cn(
                      'flex h-7 w-7 shrink-0 items-center justify-center rounded-md',
                      selected ? 'bg-muted' : 'bg-muted/70',
                    )}
                  >
                    <Icon className="h-3.5 w-3.5 text-muted-foreground" aria-hidden />
                  </span>
                  <span className={cn('truncate text-sm', selected ? 'font-semibold' : 'font-medium')}>
                    {item.label}
                  </span>
                </button>
              );
            })}
          </div>
        </div>
      ))}
    </nav>
  );
}
